import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import { fetchMeliIntegration } from '@/utils/meliIntegration';

import {
  Commerce,
  CommerceApi,
  NewCommerce,
  UpdateCommerce,
  CommerceFilters,
  MeliSellerListItem,
} from '../models';
import { mapCommerceApiToModel } from '../adapters';
import type { MeliSellersByCommerceCode } from '../utils/commerceListUtils';

const ALL_COMMERCES_PAGE_SIZE = 500;
const MELI_SELLERS_PAGE_SIZE = 500;

export interface CommerceSummary {
  id: string;
  name: string;
}

const buildCommercesParams = (filters?: CommerceFilters) => ({
  ...(filters?.name &&
    filters.name.trim() !== '' && { name: filters.name.trim() }),
  ...(filters?.businessName &&
    filters.businessName.trim() !== '' && {
      businessName: filters.businessName.trim(),
    }),
  ...(filters?.taxNumber &&
    filters.taxNumber.trim() !== '' && {
      taxNumber: filters.taxNumber.trim(),
    }),
  ...(filters?.status &&
    filters.status.trim() !== '' && { status: filters.status.trim() }),
  ...(filters?.addressId &&
    filters.addressId.trim() !== '' && {
      addressId: filters.addressId.trim(),
    }),
  ...(filters?.externalId &&
    filters.externalId.trim() !== '' && {
      externalId: filters.externalId.trim(),
    }),
  ...(filters?.businessType &&
    filters.businessType.trim() !== '' && {
      businessType: filters.businessType.trim(),
    }),
  ...(filters?.businessKey &&
    filters.businessKey.trim() !== '' && {
      businessKey: filters.businessKey.trim(),
    }),
  ...(filters?.enabled !== undefined && {
    enabled: filters.enabled,
  }),
  ...(filters?.commerceGroupId &&
    filters.commerceGroupId.trim() !== '' && {
      commerceGroupId: filters.commerceGroupId.trim(),
    }),
  ...(filters?.logisticOperatorId &&
    filters.logisticOperatorId.trim() !== '' && {
      logisticOperatorId: filters.logisticOperatorId.trim(),
    }),
  ...(filters?.email &&
    filters.email.trim() !== '' && { email: filters.email.trim() }),
  ...(filters?.phone &&
    filters.phone.trim() !== '' && { phone: filters.phone.trim() }),
  ...(filters?.integrationCodes && {
    integrationCodes: filters.integrationCodes.join(','),
  }),
});

const commercesService = createApi({
  reducerPath: 'commercesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['Commerce', 'Commerces'] as const,
  endpoints: (builder) => ({
    getCommerces: builder.query<
      PaginatedResponse<Commerce>,
      {
        paginated: PaginatedRequest;
        filters?: CommerceFilters;
      }
    >({
      query: ({ paginated, filters }) => ({
        url: '/commerces',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...buildCommercesParams(filters),
        },
      }),
      transformResponse: (response: PaginatedResponse<CommerceApi>) => ({
        ...response,
        items: response.items.map(mapCommerceApiToModel),
      }),
      providesTags: ['Commerces'],
    }),
    getAllCommerces: builder.query<Commerce[], { filters?: CommerceFilters }>({
      queryFn: async ({ filters }, _api, _extraOptions, baseQuery) => {
        const commerces: Commerce[] = [];
        for (let offset = 0; ; offset += ALL_COMMERCES_PAGE_SIZE) {
          const result = await baseQuery({
            url: '/commerces',
            params: {
              limit: ALL_COMMERCES_PAGE_SIZE,
              offset,
              ...buildCommercesParams(filters),
            },
          });
          if (result.error) return { error: result.error };
          const page = result.data as PaginatedResponse<CommerceApi>;
          commerces.push(...page.items.map(mapCommerceApiToModel));
          if (
            page.items.length < ALL_COMMERCES_PAGE_SIZE ||
            commerces.length >= page.count
          ) {
            break;
          }
        }
        return { data: commerces };
      },
    }),
    getMeliSellersByCommerceCode: builder.query<
      MeliSellersByCommerceCode,
      void
    >({
      queryFn: async () => {
        const sellersByCode: MeliSellersByCommerceCode = {};
        for (let offset = 0; ; offset += MELI_SELLERS_PAGE_SIZE) {
          const result = await fetchMeliIntegration<{
            total: number;
            sellers: MeliSellerListItem[];
          }>(`/sellers?limit=${MELI_SELLERS_PAGE_SIZE}&offset=${offset}`);
          if (result.error) return { error: result.error };
          const { total, sellers } = result.data;
          for (const seller of sellers) {
            const code = seller.businessCommerceId?.trim();
            if (!code) continue;
            sellersByCode[code] = [...(sellersByCode[code] ?? []), seller];
          }
          if (
            sellers.length < MELI_SELLERS_PAGE_SIZE ||
            offset + sellers.length >= total
          ) {
            break;
          }
        }
        return { data: sellersByCode };
      },
    }),
    getCommerceSummariesUnscoped: builder.query<
      PaginatedResponse<CommerceSummary>,
      { name?: string; ids?: string[]; limit?: number }
    >({
      query: ({ name, ids, limit }) => ({
        url: '/commerces/summaries/unscoped',
        params: {
          ...(name?.trim() && { name: name.trim() }),
          ...(ids !== undefined && { ids: ids.join(',') }),
          ...(limit !== undefined && { limit }),
        },
      }),
    }),
    getCommerceById: builder.query<Commerce, { commerceId: string }>({
      query: ({ commerceId }) => ({
        url: `/commerces/${commerceId}`,
        params: {
          include:
            'address,address.neighborhood,address.neighborhood.city,address.neighborhood.city.stateOrDepartment,address.lat,address.long',
        },
      }),
      transformResponse: (response: CommerceApi) =>
        mapCommerceApiToModel(response),
      providesTags: (_result, _error, arg) => [
        { type: 'Commerce', id: arg.commerceId },
        'Commerces',
      ],
    }),
    createCommerce: builder.mutation<Commerce, NewCommerce>({
      query: (body) => ({
        url: '/commerces',
        method: 'POST',
        body,
      }),
      transformResponse: (response: CommerceApi) =>
        mapCommerceApiToModel(response),
      invalidatesTags: ['Commerces'],
    }),
    updateCommerce: builder.mutation<
      Commerce,
      { commerceId: string; data: UpdateCommerce }
    >({
      query: ({ commerceId, data }) => ({
        url: `/commerces/${commerceId}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: CommerceApi) =>
        mapCommerceApiToModel(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Commerce', id: arg.commerceId },
        'Commerces',
      ],
    }),
    assignCommerceGroup: builder.mutation<
      Commerce,
      { commerceId: string; commerceGroupId: string | null }
    >({
      query: ({ commerceId, commerceGroupId }) => ({
        url: `/commerces/${commerceId}/commerce-group`,
        method: 'PUT',
        body: { commerceGroupId },
      }),
      transformResponse: (response: CommerceApi) =>
        mapCommerceApiToModel(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Commerce', id: arg.commerceId },
        'Commerces',
      ],
    }),
    deleteCommerce: builder.mutation<void, string>({
      query: (commerceId) => ({
        url: `/commerces/${commerceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Commerce', 'Commerces'],
    }),
  }),
});

export default commercesService;
export const {
  useGetCommercesQuery,
  useLazyGetAllCommercesQuery,
  useGetMeliSellersByCommerceCodeQuery,
  useLazyGetMeliSellersByCommerceCodeQuery,
  useGetCommerceSummariesUnscopedQuery,
  useGetCommerceByIdQuery,
  useCreateCommerceMutation,
  useUpdateCommerceMutation,
  useAssignCommerceGroupMutation,
  useDeleteCommerceMutation,
} = commercesService;
